// Sample problems data for each programming language
// This will be expanded with more problems over time

export const problemsData = {
    verilog: [
        {
            id: 1,
            title: "Simple AND Gate",
            description: "Design a simple 2-input AND gate module in Verilog. The module should have two inputs (a, b) and one output (y).",
            type: "coding",
            difficulty: "Easy",
            initialCode: `module and_gate(
    input a,
    input b,
    output y
);
    // Write your code here
    
endmodule`,
            hints: [
                "Use the assign statement for combinational logic",
                "The AND operation in Verilog is represented by &"
            ],
            solution: `module and_gate(
    input a,
    input b,
    output y
);
    assign y = a & b;
endmodule`,
            testCases: "Test with inputs: (0,0), (0,1), (1,0), (1,1)",
            testbenchCode: `\`timescale 1ns/1ps
module tb_and_gate;
    reg a;
    reg b;
    wire y;

    // Instantiate DUT
    and_gate dut (
        .a(a),
        .b(b),
        .y(y)
    );

    initial begin
        a = 0; b = 0; #10;
        if (y !== 0) $display("FAIL: expected y=0 for a=0, b=0; got y=%b", y);
        a = 0; b = 1; #10;
        if (y !== 0) $display("FAIL: expected y=0 for a=0, b=1; got y=%b", y);
        a = 1; b = 0; #10;
        if (y !== 0) $display("FAIL: expected y=0 for a=1, b=0; got y=%b", y);
        a = 1; b = 1; #10;
        if (y !== 1) $display("FAIL: expected y=1 for a=1, b=1; got y=%b", y);
        
        $display("Simulation complete.");
        $finish;
    end
    
    initial begin
        $dumpfile("dump.vcd");
        $dumpvars(0, tb_and_gate);
    end
endmodule`
        },
        {
            id: 2,
            title: "4-bit Adder",
            description: "Create a 4-bit binary adder that takes two 4-bit inputs and produces a 4-bit sum and a carry output.",
            type: "coding",
            difficulty: "Medium",
            initialCode: `module adder_4bit(
    input [3:0] a,
    input [3:0] b,
    output [3:0] sum,
    output carry
);
    // Write your code here
    
endmodule`,
            hints: [
                "Use concatenation to combine sum and carry",
                "Consider using the + operator for addition"
            ],
            solution: `module adder_4bit(
    input [3:0] a,
    input [3:0] b,
    output [3:0] sum,
    output carry
);
    assign {carry, sum} = a + b;
endmodule`,
            testbenchCode: `\`timescale 1ns/1ps
module tb_adder_4bit;
    reg [3:0] a;
    reg [3:0] b;
    wire [3:0] sum;
    wire carry;

    // Instantiate DUT
    adder_4bit dut (
        .a(a),
        .b(b),
        .sum(sum),
        .carry(carry)
    );

    initial begin
        a = 4'd2; b = 4'd3; #10;
        if (sum !== 4'd5 || carry !== 1'b0) 
            $display("FAIL: expected sum=5 carry=0 for a=2, b=3; got sum=%d carry=%b", sum, carry);
        a = 4'd15; b = 4'd1; #10;
        if (sum !== 4'd0 || carry !== 1'b1) 
            $display("FAIL: expected sum=0 carry=1 for a=15, b=1; got sum=%d carry=%b", sum, carry);
        
        $display("Simulation complete.");
        $finish;
    end

    initial begin
        $dumpfile("dump.vcd");
        $dumpvars(0, tb_adder_4bit);
    end
endmodule`
        },
        {
            id: 3,
            title: "D Flip-Flop",
            description: "Implement a D flip-flop with synchronous reset. The flip-flop should update on the positive edge of the clock.",
            type: "coding",
            difficulty: "Medium",
            initialCode: `module d_flipflop(
    input clk,
    input reset,
    input d,
    output reg q
);
    // Write your code here
    
endmodule`,
            hints: [
                "Use always @(posedge clk) for synchronous logic",
                "Check reset condition first inside the always block"
            ],
            solution: `module d_flipflop(
    input clk,
    input reset,
    input d,
    output reg q
);
    always @(posedge clk) begin
        if (reset)
            q <= 1'b0;
        else
            q <= d;
    end
endmodule`,
            testbenchCode: `\`timescale 1ns/1ps
module tb_d_flipflop;
    reg clk;
    reg reset;
    reg d;
    wire q;

    // Instantiate DUT
    d_flipflop dut (
        .clk(clk),
        .reset(reset),
        .d(d),
        .q(q)
    );

    // Clock generator
    always #5 clk = ~clk;

    initial begin
        clk = 0;
        reset = 1;
        d = 0;
        #12;
        reset = 0;
        d = 1;
        #10;
        if (q !== 1'b1) $display("FAIL: expected q=1 after posedge with d=1; got q=%b", q);
        d = 0;
        #10;
        if (q !== 1'b0) $display("FAIL: expected q=0 after posedge with d=0; got q=%b", q);
        reset = 1;
        #10;
        if (q !== 1'b0) $display("FAIL: expected q=0 after reset; got q=%b", q);
        
        $display("Simulation complete.");
        $finish;
    end

    initial begin
        $dumpfile("dump.vcd");
        $dumpvars(0, tb_d_flipflop);
    end
endmodule`
        }
    ],
    systemverilog: [
        {
            id: 1,
            title: "Basic Testbench",
            description: "Create a simple testbench for a 2-input AND gate using SystemVerilog. Include initial block and display statements.",
            type: "coding",
            difficulty: "Easy",
            initialCode: `module tb_and_gate;
    logic a, b, y;
    
    // Instantiate AND gate here
    
    initial begin
        // Write test cases here
        
    end
endmodule`,
            solution: `module tb_and_gate;
    logic a, b, y;
    
    and_gate dut(.a(a), .b(b), .y(y));
    
    initial begin
        a = 0; b = 0; #10;
        $display("a=%b, b=%b, y=%b", a, b, y);
        a = 0; b = 1; #10;
        $display("a=%b, b=%b, y=%b", a, b, y);
        a = 1; b = 0; #10;
        $display("a=%b, b=%b, y=%b", a, b, y);
        a = 1; b = 1; #10;
        $display("a=%b, b=%b, y=%b", a, b, y);
    end
endmodule`
        },
        {
            id: 2,
            title: "Interface Definition",
            description: "Define a simple AXI-like interface with valid, ready, and data signals using SystemVerilog interface construct.",
            type: "coding",
            difficulty: "Medium",
            initialCode: `interface simple_bus;
    // Define signals here
    
endinterface`,
            solution: `interface simple_bus;
    logic valid;
    logic ready;
    logic [31:0] data;
    
    modport master (output valid, data, input ready);
    modport slave (input valid, data, output ready);
endinterface`
        }
    ],
    vhdl: [
        {
            id: 1,
            title: "2-to-1 Multiplexer",
            description: "Design a 2-to-1 multiplexer in VHDL. The multiplexer should select between two inputs based on a select signal.",
            type: "coding",
            difficulty: "Easy",
            initialCode: `library IEEE;
use IEEE.STD_LOGIC_1164.ALL;

entity mux_2to1 is
    Port ( a : in STD_LOGIC;
           b : in STD_LOGIC;
           sel : in STD_LOGIC;
           y : out STD_LOGIC);
end mux_2to1;

architecture Behavioral of mux_2to1 is
begin
    -- Write your code here
    
end Behavioral;`,
            solution: `library IEEE;
use IEEE.STD_LOGIC_1164.ALL;

entity mux_2to1 is
    Port ( a : in STD_LOGIC;
           b : in STD_LOGIC;
           sel : in STD_LOGIC;
           y : out STD_LOGIC);
end mux_2to1;

architecture Behavioral of mux_2to1 is
begin
    y <= a when sel = '0' else b;
end Behavioral;`
        }
    ],
    matlab: [
        {
            id: 1,
            title: "Signal Filtering",
            description: "Create a MATLAB function that applies a simple moving average filter to a signal. The function should take a signal vector and window size as inputs.",
            type: "coding",
            difficulty: "Easy",
            initialCode: `function filtered_signal = moving_average(signal, window_size)
    % Write your code here
    
end`,
            solution: `function filtered_signal = moving_average(signal, window_size)
    filtered_signal = filter(ones(1, window_size)/window_size, 1, signal);
end`
        }
    ],
    assembly: [
        {
            id: 1,
            title: "Simple Addition",
            description: "Write an ARM assembly program to add two numbers stored in registers R0 and R1, and store the result in R2.",
            type: "coding",
            difficulty: "Easy",
            initialCode: `; ARM Assembly
; Add two numbers
    
    ; Write your code here
    `,
            solution: `; ARM Assembly
; Add two numbers
    MOV R0, #5      ; Load first number
    MOV R1, #3      ; Load second number
    ADD R2, R0, R1  ; Add and store result`
        }
    ],
    c: [
        {
            id: 1,
            title: "LED Blink (Embedded)",
            description: "Write a C function to toggle an LED connected to a GPIO pin. Assume you have a GPIO register at address 0x40020000.",
            type: "coding",
            difficulty: "Medium",
            initialCode: `#include <stdint.h>

#define GPIO_ODR (*(volatile uint32_t*)0x40020000)

void toggle_led(uint8_t pin) {
    // Write your code here
    
}`,
            solution: `#include <stdint.h>

#define GPIO_ODR (*(volatile uint32_t*)0x40020000)

void toggle_led(uint8_t pin) {
    GPIO_ODR ^= (1 << pin);
}`
        },
        {
            id: 2,
            title: "Array Sum",
            description: "Write a C function that calculates the sum of all elements in an integer array.",
            type: "coding",
            difficulty: "Easy",
            initialCode: `#include <stdio.h>

int array_sum(int arr[], int size) {
    // Write your code here
    
}`,
            solution: `#include <stdio.h>

int array_sum(int arr[], int size) {
    int sum = 0;
    for(int i = 0; i < size; i++) {
        sum += arr[i];
    }
    return sum;
}`
        }
    ],
    cpp: [
        {
            id: 1,
            title: "Class for Complex Numbers",
            description: "Create a C++ class to represent complex numbers with methods for addition and multiplication.",
            type: "coding",
            difficulty: "Medium",
            initialCode: `#include <iostream>
using namespace std;

class Complex {
private:
    double real;
    double imag;
    
public:
    // Write your code here
    
};`,
            solution: `#include <iostream>
using namespace std;

class Complex {
private:
    double real;
    double imag;
    
public:
    Complex(double r = 0, double i = 0) : real(r), imag(i) {}
    
    Complex add(Complex c) {
        return Complex(real + c.real, imag + c.imag);
    }
    
    Complex multiply(Complex c) {
        return Complex(real * c.real - imag * c.imag,
                      real * c.imag + imag * c.real);
    }
    
    void display() {
        cout << real << " + " << imag << "i" << endl;
    }
};`
        }
    ],
    python: [
        {
            id: 1,
            title: "Signal Processing",
            description: "Write a Python function to calculate the Fast Fourier Transform (FFT) of a signal using NumPy.",
            type: "coding",
            difficulty: "Easy",
            initialCode: `import numpy as np

def calculate_fft(signal):
    # Write your code here
    pass`,
            solution: `import numpy as np

def calculate_fft(signal):
    return np.fft.fft(signal)`
        },
        {
            id: 2,
            title: "Data Analysis",
            description: "Create a function that reads sensor data from a list and returns the mean, median, and standard deviation.",
            type: "coding",
            difficulty: "Easy",
            initialCode: `import numpy as np

def analyze_sensor_data(data):
    # Write your code here
    pass`,
            solution: `import numpy as np

def analyze_sensor_data(data):
    mean = np.mean(data)
    median = np.median(data)
    std_dev = np.std(data)
    return {'mean': mean, 'median': median, 'std_dev': std_dev}`
        }
    ],
    uvm: [
        {
            id: 1,
            title: "UVM Object Creation",
            description: "Design a simple custom transaction class named `my_transaction` that extends `uvm_sequence_item`. Add a random 8-bit `data` field, register it with the factory using `uvm_object_utils`, and implement the constructor.",
            type: "coding",
            difficulty: "Easy",
            initialCode: `\`include "uvm_macros.svh"
import uvm_pkg::*;

class my_transaction extends uvm_sequence_item;
  // Write your code here
  
endclass`,
            solution: `\`include "uvm_macros.svh"
import uvm_pkg::*;

class my_transaction extends uvm_sequence_item;
  rand bit [7:0] data;

  \`uvm_object_utils_begin(my_transaction)
    \`uvm_field_int(data, UVM_ALL_ON)
  \`uvm_object_utils_end

  function new(string name = "my_transaction");
    super.new(name);
  endfunction
endclass`
        },
        {
            id: 2,
            title: "UVM Testbench Component",
            description: "Define a UVM driver component class named `my_driver` that extends `uvm_driver`. Include the standard class constructor and register it with the UVM factory using `uvm_component_utils`.",
            type: "coding",
            difficulty: "Medium",
            initialCode: `\`include "uvm_macros.svh"
import uvm_pkg::*;

class my_driver extends uvm_driver;
  // Write your code here
  
endclass`,
            solution: `\`include "uvm_macros.svh"
import uvm_pkg::*;

class my_driver extends uvm_driver;
  \`uvm_component_utils(my_driver)

  function new(string name = "my_driver", uvm_component parent = null);
    super.new(name, parent);
  endfunction
endclass`
        }
    ]
};

// Helper function to get problems for a specific language
export const getProblemsByLanguage = (language) => {
    return problemsData[language] || [];
};

// Helper function to get a specific problem
export const getProblemById = (language, problemId) => {
    const problems = getProblemsByLanguage(language);
    return problems.find(p => p.id === problemId);
};
